import { List } from '@elipssolution/harfang';
import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { ExternalApplicationDetails } from './types/externalApplication';
import { getAppDetails } from './utils/appDetailsUtil';
import { WidgetExternalApplicationsContext } from './utils/context';
import { WidgetExpandedComponentProps, WidgetExternalApplicationsConfigurationType } from '../../../types/widget';
import { RenderModeEnum } from '../../../utils/widgets';
import { useSession } from '../../components/session/SessionContext';
import WidgetImagesProvider from '../../settings/widgets/provider/WidgetImagesProvider';
import { UserTypeEnum } from '../../types/user';

const LIST_ROW_HEIGHT = 52;

const widgetContextValue = { renderMode: RenderModeEnum.LIST };

const ExpandedWidgetExternalApplications = ({
	configuration,
}: WidgetExpandedComponentProps<WidgetExternalApplicationsConfigurationType>) => {
	const { shape } = useTheme();
	const {
		user: { type: userType },
	} = useSession();

	const externalApplications = useMemo(
		() =>
			(configuration?.applications ?? [])
				.sort((a, b) => a.name.localeCompare(b.name))
				.reduce((acc, application) => {
					const { name, specific } = application;

					return (userType === UserTypeEnum.INTERNAL && !application.isInternal) ||
						(userType === UserTypeEnum.EXTERNAL && !application.isExternal) ||
						!application.isAvailableOnMobileFormat
						? acc
						: ([
								...acc,
								getAppDetails(application)?.({
									name,
									specific,
								}),
						  ] as ExternalApplicationDetails[]);
				}, [] as ExternalApplicationDetails[]),
		[configuration?.applications, userType],
	);

	const listStyle = {
		height:
			externalApplications.length === 0
				? '100%'
				: // Height of every row + their bottom Divider, and the last row doesn't have a bottom Divider
				  `calc((${LIST_ROW_HEIGHT}px * ${externalApplications.length}) + ${externalApplications.length}px - 1px)`,
		borderRadius: shape.borderRadius * 2,
		overflow: 'hidden',
	};

	const dataSource = useCallback(
		() =>
			Promise.resolve({
				items: externalApplications,
				count: externalApplications.length,
			}),
		[externalApplications],
	);

	const rowRenderer = ({ component }: ExternalApplicationDetails) => component ?? <div />;

	const handleItemClick = ({ url }: ExternalApplicationDetails) => {
		url && window.open(url, '_blank');
	};

	return (
		<WidgetExternalApplicationsContext.Provider value={widgetContextValue}>
			<WidgetImagesProvider>
				<List style={listStyle} dataSource={dataSource} rowRenderer={rowRenderer} onRowClick={handleItemClick} />
			</WidgetImagesProvider>
		</WidgetExternalApplicationsContext.Provider>
	);
};

export default ExpandedWidgetExternalApplications;
